import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import {
  AUTOFILL_DOC,
  INITIAL_SAVE,
  NEW_PLAN_ID,
} from 'src/constants/plans.constants'
import { mergeDocumentsForPropagation } from 'src/lib/utilities'
import { IPlan, SupportingDocuments } from 'src/types/plan.interface'
import { IShellPlan } from 'src/types/shell.interface'
import { IUnitPlan } from 'src/types/unit.interface'

import {
  initialShellAreaData,
  initialShellGarageData,
  initialShellOverviewData,
  initialShellStructureData,
  initialShellStructureSpecifications,
  initialSpecifications,
  initialUnitAdditionalFacilities,
  initialUnitAdditionalRooms,
  initialUnitAreaData,
  initialUnitGarageData,
  initialUnitOverviewData,
  initialUnitPriceData,
  initialUnitStructureData,
  initialUnitStructureSpecifications,
} from '../redux/slice/createPlanIntialValues'

const getOverridePlan = (
  plan: IPlan,
  otherPlan: IPlan,
  isDocumentPropagationEnabled: boolean
): IPlan => {
  const {
    _etag,
    additionalOptionsData,
    additionalPlanAttributes,
    hidden,
    isCeilingHeightsRequired,
    planMasterData,
    specifications,
    supplementalDocuments,
  } = plan

  if (!otherPlan) {
    return plan
  }

  const {
    additionalOptionsData: multiAdditionalOptionsData,
    additionalPlanAttributes: multiAdditionalPlanAttributes,
    planMasterData: multiPlanMasterData,
    specifications: multiSpecifications,
    supplementalDocuments: multiSupplementDocuments,
  } = otherPlan

  return {
    _etag,
    additionalOptionsData: {
      ...additionalOptionsData,
      ...multiAdditionalOptionsData,
    },
    additionalPlanAttributes: {
      ...additionalPlanAttributes,
      ...multiAdditionalPlanAttributes,
    },
    hidden,
    isCeilingHeightsRequired,
    planMasterData: {
      ...planMasterData,
      ...multiPlanMasterData,
    },
    specifications: Object.keys(multiSpecifications).some(
      (specs) => !isEmpty(multiSpecifications[specs])
    )
      ? multiSpecifications
      : specifications,
    supplementalDocuments: getOverriddenSupplementalDocuments(
      supplementalDocuments,
      multiSupplementDocuments,
      isDocumentPropagationEnabled
    ),
  }
}

const getOverrideShellPlan = (
  plan: IShellPlan,
  otherPlan: IShellPlan,
  isDocumentPropagationEnabled: boolean
): IShellPlan => {
  const {
    _etag,
    communityPlanId,
    createdByCorporate,
    garages,
    hidden,
    parentInfo,
    shellArea,
    shellOverview,
    shellStructure,
    specifications,
    structureSpecifications,
    submitted,
    supplementalDocuments,
    versionId,
  } = plan

  if (!otherPlan) {
    return plan
  }

  const {
    garages: multiGarage,
    shellArea: multiArea,
    shellOverview: multiOverview,
    shellStructure: multiStructure,
    specifications: multiSpecifications,
    structureSpecifications: multiStructureSpecifications,
    supplementalDocuments: multiSupplementDocuments,
  } = otherPlan

  return {
    _etag,
    communityPlanId,
    createdByCorporate,
    garages: { ...garages, ...multiGarage },
    hidden,
    parentInfo,
    planCreatedBy: '',
    planCreatedDateTime: '',
    shellArea: { ...shellArea, ...multiArea },
    shellOverview: {
      ...shellOverview,
      ...multiOverview,
    },
    shellStructure: {
      ...shellStructure,
      ...multiStructure,
    },
    specifications:
      multiSpecifications &&
      Object.keys(multiSpecifications).some(
        (specs) => !isEmpty(multiSpecifications[specs])
      )
        ? multiSpecifications
        : specifications,
    structureSpecifications: {
      ...structureSpecifications,
      ...multiStructureSpecifications,
    },
    submitted,
    supplementalDocuments: getOverriddenSupplementalDocuments(
      supplementalDocuments,
      multiSupplementDocuments,
      isDocumentPropagationEnabled
    ),
    versionId,
  }
}

const getOverrideUnitPlan = (
  plan: IUnitPlan,
  otherPlan: IUnitPlan,
  isDocumentPropagationEnabled: boolean
): IUnitPlan => {
  const {
    _etag,
    communityPlanId,
    createdByCorporate,
    garages,
    parentInfo,
    specifications,
    structureSpecifications,
    submitted,
    supplementalDocuments,
    unitAdditionalFacilities,
    unitAdditionalRooms,
    unitArea,
    unitOverview,
    unitPrice,
    unitStructure,
    versionId,
  } = plan

  if (!otherPlan) {
    return plan
  }

  const {
    garages: multiGarage,
    specifications: multiSpecifications,
    structureSpecifications: multiStructureSpecifications,
    supplementalDocuments: multiSupplementDocuments,
    unitAdditionalFacilities: multiAdditionalFacilities,
    unitAdditionalRooms: multiAdditionalRooms,
    unitArea: multiArea,
    unitOverview: multiOverview,
    unitPrice: multiPrice,
    unitStructure: multiStructure,
  } = otherPlan

  return {
    _etag,
    communityPlanId,
    createdByCorporate,
    garages: { ...garages, ...multiGarage },
    parentInfo,
    planCreatedBy: '',
    planCreatedDateTime: '',
    specifications:
      multiSpecifications &&
      Object.keys(multiSpecifications).some(
        (specs) => !isEmpty(multiSpecifications[specs])
      )
        ? multiSpecifications
        : specifications,
    structureSpecifications: {
      ...structureSpecifications,
      ...multiStructureSpecifications,
    },
    submitted,
    supplementalDocuments: getOverriddenSupplementalDocuments(
      supplementalDocuments,
      multiSupplementDocuments,
      isDocumentPropagationEnabled
    ),
    unitAdditionalFacilities: {
      ...unitAdditionalFacilities,
      ...multiAdditionalFacilities,
    },
    unitAdditionalRooms: {
      ...unitAdditionalRooms,
      ...multiAdditionalRooms,
    },
    unitArea: { ...unitArea, ...multiArea },
    unitOverview: {
      ...unitOverview,
      ...multiOverview,
    },
    unitPrice: {
      ...unitPrice,
      ...multiPrice,
    },
    unitStructure: {
      ...unitStructure,
      ...multiStructure,
    },
    versionId,
  }
}

const getIsCreatePlanFormDirty = (plans: IPlan | Record<string, IPlan>) => {
  return Object.values(plans).some(
    (plan) =>
      !(
        isEmpty(plan.planMasterData) &&
        isEmpty(plan.additionalOptionsData) &&
        isEmpty(plan.additionalPlanAttributes) &&
        isEqual(plan.specifications, initialSpecifications)
      )
  )
}

const getIsCreateShellPlanFormDirty = (
  shells: IShellPlan | Record<string, IShellPlan>
) => {
  return Object.values(shells).some(
    (shell) =>
      !(
        isEqual(shell.shellOverview, initialShellOverviewData) &&
        isEqual(shell.shellArea, initialShellAreaData) &&
        isEqual(shell.garages, initialShellGarageData) &&
        isEqual(shell.shellStructure, initialShellStructureData) &&
        isEqual(
          shell.structureSpecifications,
          initialShellStructureSpecifications
        )
      )
  )
}

const getIsCreateUnitPlanFormDirty = (
  units: IUnitPlan | Record<string, IUnitPlan>
) => {
  return Object.values(units).some(
    (unit) =>
      !(
        isEqual(unit.unitOverview, initialUnitOverviewData) &&
        isEqual(unit.unitArea, initialUnitAreaData) &&
        isEqual(unit.garages, initialUnitGarageData) &&
        isEqual(unit.unitStructure, initialUnitStructureData) &&
        isEqual(unit.unitAdditionalRooms, initialUnitAdditionalRooms) &&
        isEqual(unit.unitPrice, initialUnitPriceData) &&
        isEqual(
          unit.unitAdditionalFacilities,
          initialUnitAdditionalFacilities
        ) &&
        isEqual(
          unit.structureSpecifications,
          initialUnitStructureSpecifications
        )
      )
  )
}

const getOverriddenSupplementalDocuments = (
  attachedDocuments: SupportingDocuments[],
  modifiedDocuments?: SupportingDocuments[],
  isDocumentPropagationEnabled?: boolean
): SupportingDocuments[] => {
  // Normalize null/undefined inputs to empty array
  const normalizedAttached = attachedDocuments || []
  const normalizedModified = modifiedDocuments === null ? [] : modifiedDocuments

  if (normalizedModified === undefined) {
    // Documents not loaded yet - use primary elevation documents
    return normalizedAttached
  } else if (isDocumentPropagationEnabled) {
    return mergeDocumentsForPropagation(normalizedAttached, normalizedModified)
  } else {
    // Propagation disabled - use only secondary elevation's own documents
    return normalizedModified
  }
}

const getAutoFillDocInitialSave = () => {
  return localStorage.getItem(AUTOFILL_DOC)
}

const setAutoFillDocInitialSave = (value: string) => {
  return localStorage.setItem(AUTOFILL_DOC, value)
}

const removeAutoFillDocInitialSave = () => {
  return localStorage.removeItem(AUTOFILL_DOC)
}

const getCopiedPlanInitialSave = () => {
  return localStorage.getItem(INITIAL_SAVE)
}

const setCopiedPlanInitialSave = (value: string) => {
  return localStorage.setItem(INITIAL_SAVE, value)
}

const removeCopiedPlanInitialSave = () => {
  return localStorage.removeItem(INITIAL_SAVE)
}

const getCopiedPlanID = () => {
  return localStorage.getItem(NEW_PLAN_ID)
}

const setCopiedPlanID = (value: string) => {
  return localStorage.setItem(NEW_PLAN_ID, value)
}

const removeCopiedPlanID = () => {
  return localStorage.removeItem(NEW_PLAN_ID)
}

const renderBoolean = (value: boolean | null, emptyValue: string = '-') =>
  value === null ? emptyValue : value ? 'Y' : 'N'

export {
  getAutoFillDocInitialSave,
  getCopiedPlanID,
  getCopiedPlanInitialSave,
  getIsCreatePlanFormDirty,
  getIsCreateShellPlanFormDirty,
  getIsCreateUnitPlanFormDirty,
  getOverridePlan,
  getOverrideShellPlan,
  getOverrideUnitPlan,
  removeAutoFillDocInitialSave,
  removeCopiedPlanID,
  removeCopiedPlanInitialSave,
  renderBoolean,
  setAutoFillDocInitialSave,
  setCopiedPlanID,
  setCopiedPlanInitialSave,
}
